import type { FC, ReactNode } from 'react'
import type { ArtistProfile } from '@/types/content'

import { cms } from '@/lib/cms'
import { MoonPhase } from '@/components/media/moon-phase'

interface TwoWorldsProps {
    artist: ArtistProfile
}

/** Serene and Corpse: the two worlds the name holds, side by side. */
export const TwoWorlds: FC<TwoWorldsProps> = ({ artist }): ReactNode => {
    if (!artist.serene_text && !artist.corpse_text) return null

    return (
        <section id='two-worlds' aria-label='The two worlds of Serene Corpse' className='relative overflow-hidden border-y border-white/[0.05]'>
            <div className='grid md:grid-cols-2'>
                <div className='reveal relative flex flex-col justify-center px-6 py-20 sm:px-12 lg:px-20 lg:py-28'>
                    <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,rgba(233,228,218,0.06),transparent_60%)]' aria-hidden='true' />
                    <p className='eyebrow text-gold/80'>The first world</p>
                    <p className='mt-5 font-brand text-4xl uppercase tracking-[0.24em] text-ivory sm:text-5xl'>Serene</p>
                    {artist.serene_text && (
                        <p className='mt-6 max-w-sm font-heading text-2xl italic leading-snug text-ivory/75'>
                            <span {...cms('artist', artist.id, 'serene_text')}>{artist.serene_text}</span>
                        </p>
                    )}
                </div>

                <div className='reveal relative flex flex-col justify-center border-t border-white/[0.05] bg-[#0d0a0b] px-6 py-20 sm:px-12 md:border-l md:border-t-0 lg:px-20 lg:py-28'>
                    <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_60%,rgba(114,43,59,0.28),transparent_60%)]' aria-hidden='true' />
                    <p className='eyebrow relative text-[#c98a98]'>The second world</p>
                    <p className='relative mt-5 font-brand text-4xl uppercase tracking-[0.24em] text-ivory sm:text-5xl'>Corpse</p>
                    {artist.corpse_text && (
                        <p className='relative mt-6 max-w-sm font-heading text-2xl italic leading-snug text-ivory/75'>
                            <span {...cms('artist', artist.id, 'corpse_text')}>{artist.corpse_text}</span>
                        </p>
                    )}
                </div>
            </div>

            <div className='pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 rounded-full bg-obsidian p-4 ring-1 ring-white/10 md:block' aria-hidden='true'>
                <MoonPhase phase={0.5} size={44} glow />
            </div>
        </section>
    )
}
